import { cn } from '@/app/lib/utils';
import CustomSelect, { Option, CustomSelectProps } from '../ui/CustomSelect';
import { useEffect, useMemo, useState } from 'react';
import { getTargetLists, getTargetList } from '@/api/target-list';
import { useMutation, useQuery } from '@tanstack/react-query';
import Icon from '../ui/Icon';
import { useParams } from 'next/navigation';

type Props = {
  defaultValue: any;
  label?: string;
  required?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  className?: string;
  searchable?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  placeholder?: string;
  multiple?: boolean;
  onOptionSelect: (option: any) => void;
  fetchSelectedOptionOnMount?: boolean;
  status?: string;
  excludeIds?: string[];
} & Partial<CustomSelectProps>;

const TargetlistSelect = ({
  defaultValue,
  label,
  required,
  wrapperClassName,
  labelClassName,
  className,
  searchable,
  clearable,
  disabled,
  placeholder,
  onOptionSelect,
  fetchSelectedOptionOnMount,
  multiple,
  status,
  excludeIds,
  ...props
}: Props) => {
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [isOptionCbTriggered, setIsOptionCbTriggered] = useState(false);
  const { clientId } = useParams<{ clientId: string }>();
  const { data: options, isLoading } = useQuery({
    queryKey: ['targetLists', clientId, search, status],
    queryFn: () =>
      getTargetLists({
        clientId: clientId || '',
        search: search,
        status: status,
      }),
    enabled: !!clientId,
  });
  const {
    mutate: getTargetListMutation,
    data: missingTargetList,
    isPending: isLoadingMissingTargetList,
    isError: isErrorMissingTargetList,
  } = useMutation({
    mutationFn: (id: string) => getTargetList(clientId || '', id),
    onError: error => {
      console.error('error', error);
    },
  });

  const optionsList = useMemo(() => {
    const excludedIdSet = new Set(excludeIds || []);
    const list: Option[] =
      options?.data
        ?.filter(option => !excludedIdSet.has(option.id))
        .map(option => ({
          id: option.id,
          value: option.name || '-',
          meta: {
            company_count: option.company_count || 0,
            contact_count: option.contact_count || 0,
          },
        })) || [];
    if (!multiple && selectedOption && list.find(o => o.id === selectedOption.id) === undefined) {
      list.unshift({
        id: selectedOption.id.toString(),
        value: selectedOption.value.toString(),
        prefix: selectedOption.prefix,
      });
    }
    if (multiple && selectedOptions) {
      // find missing options
      const missingOptions = selectedOptions.filter(o => !list.some(c => c.id === o.id));
      list.unshift(...missingOptions);
    }
    if (missingTargetList) {
      list.unshift({
        id: missingTargetList.id,
        value: missingTargetList.name || '-',
      });
    }
    return list;
  }, [options, selectedOption, selectedOptions, multiple, missingTargetList, excludeIds]);

  useEffect(() => {
    if (fetchSelectedOptionOnMount && defaultValue && !isOptionCbTriggered && !isLoading) {
      let values: Option | Option[] | null = null;
      if (multiple) {
        values = optionsList.filter(o => defaultValue.includes(o.id)) || [];
      } else {
        values = optionsList.find(o => o.id === defaultValue) || null;
      }
      if ((Array.isArray(values) && values.length !== 0) || values !== null) {
        if (multiple && Array.isArray(values)) {
          setSelectedOptions(values);
        } else if (!multiple && values && !Array.isArray(values)) {
          setSelectedOption(values);
        }
        onOptionSelect(values);
        setIsOptionCbTriggered(true);
      } else if (defaultValue || (Array.isArray(defaultValue) && defaultValue.length !== 0)) {
        // call api
        if (!isErrorMissingTargetList) {
          if (multiple && Array.isArray(defaultValue)) {
            // For multiple, we might need to fetch each one, but for now just fetch the first
            if (defaultValue.length > 0) {
              getTargetListMutation(defaultValue[0]);
            }
          } else {
            getTargetListMutation(defaultValue);
          }
        }
      }
    }
  }, [
    fetchSelectedOptionOnMount,
    defaultValue,
    optionsList,
    onOptionSelect,
    isOptionCbTriggered,
    isLoading,
    multiple,
    getTargetListMutation,
    isErrorMissingTargetList,
  ]);

  return (
    <div className={cn('flex flex-col', wrapperClassName)}>
      {label && (
        <label
          className={cn(
            'text-sm font-medium mb-1.5 flex items-center gap-1 text-muted-foreground',
            labelClassName
          )}
        >
          <span>
            {label}
            {required && <span className="text-red-600 ml-1">*</span>}
          </span>
          {isLoadingMissingTargetList && (
            <Icon
              name="loader-2-fill"
              size={4}
              aria-label="Loading"
              className="animate-spin w-4 h-4"
            />
          )}
        </label>
      )}
      <CustomSelect
        options={optionsList || []}
        placeholder={placeholder}
        defaultValue={defaultValue}
        onOptionSelect={option => {
          if (multiple) {
            setSelectedOptions(option as Option[]);
          } else {
            setSelectedOption(option as Option);
          }
          onOptionSelect(option);
        }}
        searchable={searchable}
        clearable={clearable}
        disabled={disabled}
        triggerClassNames={className}
        onSearch={search => {
          setSearch(search);
        }}
        searchLoading={isLoading}
        multiple={multiple}
        {...props}
      />
    </div>
  );
};

export default TargetlistSelect;
